<script setup>
import { computed, ref, watch } from 'vue';
import DOMPurify from 'dompurify';
import IModal from '@components/IModal/IModal.vue';
import useCartStore from '@vueApp/store/modules/cart';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  ticket: {
    type: Object,
    default: null,
  },
  eventId: {
    type: Number,
    required: true,
  },
  orderId: {
    type: Number,
    required: true,
  },
});

const emits = defineEmits(['update:modelValue']);

const cartStore = useCartStore();

const isLoading = ref(false);
const additionals = ref([]);
const added = ref([]);

const addedServices = computed(() => (added.value || []).filter(
  service => service.parent != null && Number(service.parent) === Number(props.ticket?.uid),
));

/* Набор услуг у каждого билета свой (БО отдаёт список по index билета),
   поэтому услуги, добавленные к другим билетам заказа, отсюда не убираем.
   Повторные добавления не ограничиваем — одинаковых подарков может быть несколько */
const availableServices = computed(() => additionals.value || []);

/* Схема ответа БО неровная — поля вытаскиваем с фолбэками */
function normalizeServices(list) {
  return (list || []).map(item => {
    const extras = item.extras || {};
    const serviceId = item.index != null ? item.index : item.service_id;
    const priceTypeId = item.price_type_id != null ? item.price_type_id : item.price_view_id;
    const name = item.name || extras.name || extras.service_name
      || (serviceId != null ? `Услуга #${serviceId}` : '');
    const price = item.price != null ? item.price : extras.price;

    return {
      serviceId,
      priceTypeId,
      name,
      /* text — полное описание (html-файл из Ленты), description — короткое */
      description: item.description || '',
      text: item.text || '',
      image: item.image || '',
      price,
    };
  });
}

/* Полное описание услуги — html-файл из Ленты; перед v-html санитайзим */
function serviceText(service) {
  return service.text || service.description || '';
}

function sanitizedText(service) {
  return DOMPurify.sanitize(serviceText(service));
}

function refresh() {
  isLoading.value = true;
  additionals.value = [];
  added.value = [];

  cartStore.getAdditionals(props.eventId, props.ticket?.uid)
    .then(res => {
      additionals.value = normalizeServices(res.additionals);
      added.value = res.added;
    })
    .finally(() => (isLoading.value = false));
}

watch(
  () => props.modelValue,
  isShown => {
    if (isShown && props.ticket) refresh();
  },
);

function onAdd(service) {
  cartStore.addAdditional({
    eventId: props.eventId,
    orderId: props.orderId,
    parentUid: props.ticket.uid,
    serviceId: service.serviceId,
    priceTypeId: service.priceTypeId,
    index: props.ticket.uid,
  }).then(res => {
    if (!res) return;
    additionals.value = normalizeServices(res.additionals);
    added.value = res.added;
  });
}

function onRemove(service) {
  cartStore.removeAdditional({
    eventId: props.eventId,
    orderId: props.orderId,
    uid: service.uid,
    index: props.ticket.uid,
  }).then(res => {
    if (!res) return;
    additionals.value = normalizeServices(res.additionals);
    added.value = res.added;
  });
}
</script>

<template>
  <i-modal
    :model-value="modelValue"
    modal-class="additionals-modal"
    @update:model-value="value => emits('update:modelValue', value)"
  >
    <template #header>
      <span class="modal-title-header">Подарки</span>
    </template>
    <template #body>
      <div v-if="ticket" class="additionals-modal__ticket">
        {{ ticket.sector }}, Ряд {{ ticket.row }}, Место {{ ticket.col }}
      </div>

      <div v-if="isLoading" class="additionals-modal__empty">Загрузка…</div>

      <template v-else-if="addedServices.length || availableServices.length">
        <div
          v-for="service in addedServices"
          :key="service.uid"
          class="additionals-modal__row additionals-modal__row_added"
        >
          <img
            v-if="service.image"
            class="additionals-modal__image"
            :src="service.image"
            :alt="service.name"
          >
          <div class="additionals-modal__info">
            <span class="additionals-modal__name">{{ service.name }}</span>
            <div
              v-if="service.text || service.description"
              class="additionals-modal__description"
              v-html="sanitizedText(service)"
            />
          </div>
          <span class="additionals-modal__price">{{ service.price }} ₽</span>
          <button
            class="additionals-modal__remove reset-btn"
            type="button"
            title="Удалить"
            @click="onRemove(service)"
          >
            <svg height="12" width="12">
              <use xlink:href="#close" />
            </svg>
          </button>
        </div>

        <div
          v-for="service in availableServices"
          :key="`${service.serviceId}-${service.priceTypeId}`"
          class="additionals-modal__row"
        >
          <img
            v-if="service.image"
            class="additionals-modal__image"
            :src="service.image"
            :alt="service.name"
          >
          <div class="additionals-modal__info">
            <span class="additionals-modal__name">{{ service.name }}</span>
            <div
              v-if="service.text || service.description"
              class="additionals-modal__description"
              v-html="sanitizedText(service)"
            />
          </div>
          <span class="additionals-modal__price">{{ service.price }} ₽</span>
          <button
            class="btn btn_primary additionals-modal__btn"
            type="button"
            @click="onAdd(service)"
          >
            Добавить
          </button>
        </div>
      </template>

      <div v-else class="additionals-modal__empty">Нет доступных услуг</div>

      <button
        class="btn btn_primary btn_full additionals-modal__continue"
        type="button"
        data-bs-dismiss="modal"
      >
        Продолжить покупки
      </button>
    </template>
  </i-modal>
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";

.additionals-modal {
  &__ticket {
    margin-bottom: 12px;
    font-weight: 700;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 10px;
    border: 1px solid $border-color;
    border-radius: 8px;

    & + & {
      margin-top: 6px;
    }

    &_added {
      background-color: $grey-third;
    }
  }

  &__image {
    flex-shrink: 0;
    width: 64px;
    height: 64px;
    border-radius: 8px;
    object-fit: contain;
  }

  &__info {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  &__name {
    font-size: 14px;
  }

  &__description {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 12px;
    line-height: 1.4;
    color: $dark-grey;
    overflow-wrap: anywhere;

    p {
      margin: 0 0 4px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__price {
    font-size: 14px;
    white-space: nowrap;
  }

  &__btn {
    padding: 4px 12px;
    font-size: 13px;
    white-space: nowrap;
  }

  &__remove {
    padding: 4px;
    color: $primary;

    &:hover {
      opacity: 0.7;
    }
  }

  &__empty {
    font-size: 14px;
    color: $dark-grey;
  }

  &__continue {
    margin-top: 16px;
  }
}
</style>

<style lang="scss">
/* Кнопка закрытия в IModal идёт в DOM перед заголовком —
   сдвигаем её в правый верхний угол модалки */
.additionals-modal {
  .btn-close {
    position: absolute;
    top: 16px;
    right: 16px;
  }
}
</style>
